import React, { useState } from 'react';
import CryptoJS from 'crypto-js';
import API from '../../../api/API';

/**
 * Elenco e gestione degli utenti.
 *
 * La password viene passata come MD5, esattamente come fa la pagina di login: e' quello che il
 * backend confronta, e mandarla in chiaro renderebbe l'utente incapace di autenticarsi. Il server
 * la rifiuta se non ha la forma di un MD5, quindi il calcolo non e' una gentilezza ma un requisito.
 */
const vuoto = { nome: '', user: '', psw: '', email: '', attivo: true, gruppi: [] };

const TabUtenti = ({ utenti, gruppi, me, onCambiato, onApriPermessi }) => {
  const [form, setForm] = useState(null);
  const [errori, setErrori] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const apriNuovo = () => {
    setErrori([]);
    setForm({ ...vuoto, modo: 'nuovo' });
  };

  const apriModifica = (u) => {
    setErrori([]);
    setForm({
      modo: 'modifica',
      id: u.id,
      nome: u.nome,
      user: u.user,
      psw: '',
      email: u.email || '',
      attivo: u.attivo,
      gruppi: (u.gruppi || []).map(g => g.id)
    });
  };

  const chiudi = () => { setForm(null); setErrori([]); };

  const mostraErrori = (res) => {
    // il backend risponde con l'elenco dei motivi: e' quello che va fatto vedere, non un generico
    // "operazione non riuscita"
    const dati = res.data;
    setErrori(Array.isArray(dati) ? dati : [res.errorMsg || 'Operazione non riuscita']);
  };

  const salva = async () => {
    setSalvando(true);
    setErrori([]);

    const corpo = {
      nome: form.nome,
      user: form.user,
      email: form.email || null,
      attivo: form.attivo,
      psw: form.psw ? CryptoJS.MD5(form.psw).toString() : null
    };

    let res;
    if (form.modo === 'nuovo') {
      res = await API.utenti.crea({ ...corpo, gruppi: form.gruppi, permessi: [] });
    } else {
      res = await API.utenti.aggiorna(form.id, corpo);
      if (res.isSuccess) {
        const suiGruppi = await API.utenti.impostaGruppi(form.id, form.gruppi);
        if (!suiGruppi.isSuccess) res = suiGruppi;
      }
    }

    setSalvando(false);

    if (!res.isSuccess) {
      mostraErrori(res);
      return;
    }

    chiudi();
    await onCambiato();
  };

  const cancella = async (u) => {
    if (!window.confirm(`Cancellare l'utente "${u.user}"? Spariscono anche i suoi permessi.`)) return;

    const res = await API.utenti.cancella(u.id);
    if (!res.isSuccess) {
      const dati = res.data;
      alert(Array.isArray(dati) ? dati.join('\n') : (res.errorMsg || 'Cancellazione non riuscita'));
      return;
    }
    await onCambiato();
  };

  const puoCreare = me?.bypass || me?.globale?.UTENTE === 'SCRITTURA'
    || (me?.gruppiTornei || []).some(a => a.permessi?.UTENTE === 'SCRITTURA')
    || (me?.edizioni || []).some(a => a.permessi?.UTENTE === 'SCRITTURA');

  return (
    <>
      <div className="utenti-barra">
        <p className="utenti-nota">
          Vedi gli utenti che hai il permesso di amministrare. Un utente senza permessi lo vede solo
          chi amministra a livello globale.
        </p>
        <button className="btn btn-primary" onClick={apriNuovo} disabled={!puoCreare}
                title={puoCreare ? 'Crea un utente' : 'Ti serve la scrittura su UTENTE in almeno un ambito'}>
          <i className="fas fa-plus"></i> Nuovo utente
        </button>
      </div>

      {form && (
        <div className="utenti-form">
          <h3>{form.modo === 'nuovo' ? 'Nuovo utente' : `Modifica di ${form.user}`}</h3>

          <div className="utenti-form-griglia">
            <label>
              Nome
              <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
            </label>
            <label>
              Username
              <input value={form.user} onChange={e => setForm({ ...form, user: e.target.value })} />
            </label>
            <label>
              Password
              <input type="password" value={form.psw} autoComplete="new-password"
                     placeholder={form.modo === 'nuovo' ? 'obbligatoria' : 'lasciare vuoto per non cambiarla'}
                     onChange={e => setForm({ ...form, psw: e.target.value })} />
            </label>
            <label>
              Email
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </label>
            <label className="utenti-form-check">
              <input type="checkbox" checked={form.attivo}
                     onChange={e => setForm({ ...form, attivo: e.target.checked })} />
              Attivo
            </label>
          </div>

          <div className="utenti-form-gruppi">
            <span className="utenti-form-etichetta">Gruppi</span>
            {gruppi.length === 0 && <em className="utenti-nota">Nessun gruppo disponibile.</em>}
            {gruppi.map(g => (
              <label key={g.id} className="utenti-chip">
                <input
                  type="checkbox"
                  checked={form.gruppi.includes(g.id)}
                  disabled={!g.gestibile}
                  title={g.gestibile ? '' : 'Questo gruppo ha permessi che tu non hai'}
                  onChange={e => setForm({
                    ...form,
                    gruppi: e.target.checked
                      ? [...form.gruppi, g.id]
                      : form.gruppi.filter(x => x !== g.id)
                  })}
                />
                {g.nome}{g.bypass ? ' (bypass)' : ''}
              </label>
            ))}
          </div>

          {errori.length > 0 && (
            <ul className="utenti-errore utenti-errore-lista">
              {errori.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}

          <div className="utenti-form-azioni">
            <button className="btn btn-primary" onClick={salva} disabled={salvando}>
              {salvando ? 'Salvataggio...' : 'Salva'}
            </button>
            <button className="btn btn-secondary" onClick={chiudi} disabled={salvando}>Annulla</button>
          </div>
        </div>
      )}

      <div className="utenti-tabella-wrap">
        <table className="utenti-tabella">
          <thead>
            <tr>
              <th>Username</th>
              <th>Nome</th>
              <th>Gruppi</th>
              <th>Celle</th>
              <th>Stato</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {utenti.map(u => (
              <tr key={u.id} className={u.attivo ? '' : 'utenti-sospeso'}>
                <td><strong>{u.user}</strong>{u.id === me?.id && <span className="utenti-io"> (tu)</span>}</td>
                <td>{u.nome}</td>
                <td>
                  {(u.gruppi || []).length === 0
                    ? <span className="utenti-nota">nessuno</span>
                    : u.gruppi.map(g => (
                        <span key={g.id} className={`utenti-tag ${g.bypass ? 'utenti-tag-bypass' : ''}`}>{g.nome}</span>
                      ))}
                </td>
                <td>{u.celle}</td>
                <td>{u.attivo ? 'attivo' : 'sospeso'}</td>
                <td className="utenti-azioni">
                  <button className="btn btn-info btn-sm" title="Permessi"
                          onClick={() => onApriPermessi(u.id, u.user)}>
                    <i className="fas fa-table"></i>
                  </button>
                  <button className="btn btn-secondary btn-sm" title={u.gestibile ? 'Modifica' : 'Ha permessi che tu non hai'}
                          disabled={!u.gestibile} onClick={() => apriModifica(u)}>
                    <i className="fas fa-pen"></i>
                  </button>
                  <button className="btn btn-delete btn-sm"
                          title={u.gestibile ? 'Cancella' : 'Ha permessi che tu non hai'}
                          disabled={!u.gestibile || u.id === me?.id} onClick={() => cancella(u)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TabUtenti;
